import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

// components and helpers
import utils from '../../../utils/utils';
import { securityActions } from './SecurityActions';
import * as confirmActions from '../../Confirmation/ConfirmationActions';
import Table from '../../Shared/Table';
import InviteDialog from './InviteDialog';
import activeUserIcon from '../../../assets/img/Group 396.svg';
import pendingUserIcon from '../../../assets/img/Group 397.svg';

// mui
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/AddRounded';
import { Chip } from '@mui/material';

const Security: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { user } = useAuth0();
    const [accessPageLimit, setAccessPageLimit] = useState<number>(10);
    const [statusPageLimit, setStatusPageLimit] = useState<number>(10);
    const [accessOffset, setAccessOffset] = useState<number>(0);
    const [statusOffset, setStatusOffset] = useState<number>(0);
    const [accessSortField, setAccessSortField] = useState<string>('userId');
    const [statusSortField, setStatusSortField] =
        useState<string>('lastUpdated');
    const [accessSortDirection, setAccessSortDirection] =
        useState<string>('ASC');
    const [statusSortDirection, setStatusSortDirection] =
        useState<string>('DESC');
    const [accessPageNumber, setAccessPageNumber] = useState<number>(0);
    const [statusPageNumber, setStatusPageNumber] = useState<number>(0);
    const [openInvite, setOpenInvite] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [actionOpen, setActionOpen] = useState<string | null>(null);
    const [changeRoleEmail, setChangeRoleEmail] = useState<string>('');
    const [changeRoleRole, setChangeRoleRole] = useState<string>('');
    const workspaceAccessList = useSelector(
        (state: any) => state.security.workspaceAccessList
    );
    const workspaceAccessListLoading = useSelector(
        (state: any) => state.security.workspaceAccessListLoading
    );
    const workspacePendingList = useSelector(
        (state: any) => state.security.workspacePendingList
    );
    const workspacePendingListLoading = useSelector(
        (state: any) => state.security.workspacePendingListLoading
    );
    const userRole = useSelector(
        (state: any) => state.workspace.workspaceUserRole
    );

    useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (changeRoleRole && changeRoleEmail && !openInvite) {
            setOpenInvite(true);
        }
    }, [changeRoleRole, changeRoleEmail, openInvite]);

    const handleFetch = () => {
        const id = location.pathname.split('/').pop().split('&')[0];
        dispatch(
            securityActions?.getWorkspaceSecurityByStatus(
                id,
                'PENDING',
                100,
                statusOffset,
                statusSortField,
                statusSortDirection
            )
        );
        dispatch(
            securityActions?.getWorkspaceAccesses(
                id,
                accessPageLimit,
                accessOffset,
                accessSortField,
                accessSortDirection
            )
        );
    };

    const handleChangeRole = (email: string, role: string) => {
        const id = location.pathname.split('/').pop().split('&')[0];
        dispatch(
            securityActions?.changeRole(
                id,
                email.toLowerCase(),
                role,
                user && user.name
            )
        );
    };

    const handleInviteUser = (email: string, role: string) => {
        const id = location.pathname.split('/').pop().split('&')[0];
        dispatch(securityActions?.inviteUser(id, email.toLowerCase(), role));
    };

    const handleTableChange = (
        type: string,
        action: string,
        tableState: any
    ) => {
        if (action !== 'propsUpdate') {
            let selectedSortField =
                type === 'ACCESS' ? accessSortField : statusSortField;
            let selectedSortDirection =
                type === 'ACCESS' ? accessSortDirection : statusSortDirection;
            const limit = tableState.rowsPerPage;
            const skip = tableState.rowsPerPage * tableState.page;
            const field = tableState.sortOrder.name || selectedSortField;
            const direction =
                (tableState.sortOrder.direction &&
                    tableState.sortOrder.direction.toUpperCase()) ||
                (selectedSortDirection && selectedSortDirection.toUpperCase());
            const page = tableState.page;

            handleFetch();

            if (type === 'ACCESS') {
                setAccessOffset(skip);
                setAccessPageLimit(limit);
                setAccessSortField(field);
                setAccessSortDirection(direction);
                setAccessPageNumber(page);
            } else {
                setStatusOffset(skip);
                setStatusPageLimit(limit);
                setStatusSortField(field);
                setStatusSortDirection(direction);
                setStatusPageNumber(page);
            }
        }
    };

    const handleActionClick = (
        event: React.MouseEvent<HTMLElement>,
        value: string
    ) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setActionOpen(value);
    };

    const handleCloseAction = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
        setActionOpen(null);
    };

    const handleAction = (
        event: React.MouseEvent<HTMLElement>,
        value: string,
        data: any
    ) => {
        event.stopPropagation();
        const inviteId = data.rowData[0];
        const userId = data.rowData[1];
        const userRole = data.rowData[2];
        const workspaceId = location.pathname.split('/').pop().split('&')[0];
        switch (value) {
            case 'ROLE':
                setChangeRoleEmail(data.rowData[1]);
                setChangeRoleRole(data.rowData[2]);
                setAnchorEl(null);
                setActionOpen(null);
                break;
            case 'REVOKE':
                dispatch(
                    confirmActions.showConfirmation(
                        'Revoke Access',
                        'Are you sure you want to revoke access to this user?',
                        'revokeAccess',
                        workspaceId,
                        userId
                    )
                );
                setAnchorEl(null);
                setActionOpen(null);
                break;
            case 'CANCEL':
                dispatch(
                    confirmActions.showConfirmation(
                        'Cancel Invitation',
                        'Are you sure you want to cancel the invitation to this user?',
                        'cancelInvitation',
                        inviteId
                    )
                );
                setAnchorEl(null);
                setActionOpen(null);
                break;
            case 'INVITE':
                dispatch(
                    confirmActions.showConfirmation(
                        'Re-Invite',
                        'Are you sure you want to resend the invitation to this user?',
                        'reInvite',
                        workspaceId,
                        userId,
                        userRole
                    )
                );
                setAnchorEl(null);
                setActionOpen(null);
                break;
            default:
                break;
        }
    };

    const activeActionOptions = [
        {
            key: 'ROLE',
            value: 'Change Role',
        },
        {
            key: 'REVOKE',
            value: 'Revoke Access',
        },
    ];
    const pendingActionOptions = [
        {
            key: 'CANCEL',
            value: 'Cancel Invitation',
        },
    ];
    const inactiveActionOptions = [
        {
            key: 'INVITE',
            value: 'Re-Send Invitation',
        },
    ];

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
            },
        },
        {
            name: 'userId',
            label: 'User ID',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'role',
            label: 'Role',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.toTitleText(value.toLowerCase());
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'status',
            label: 'Status',
            options: {
                customBodyRender: (value: string) => {
                    if (value === 'PENDING') {
                        return (
                            <Chip
                                label={utils.toTitleText(value.toLowerCase())}
                                sx={{
                                    backgroundColor: '#845EC2',
                                    color: '#fff',
                                }}
                            />
                        );
                    } else if (value === 'REJECTED' || value === 'EXPIRED') {
                        return (
                            <Chip
                                label={utils.toTitleText(value.toLowerCase())}
                                sx={{
                                    backgroundColor: '#D32F2F',
                                    color: '#fff',
                                }}
                            />
                        );
                    } else if (value === 'ACCEPTED') {
                        return (
                            <Chip
                                label={utils.toTitleText(value.toLowerCase())}
                                sx={{
                                    backgroundColor: '#325599',
                                    color: '#fff',
                                }}
                            />
                        );
                    } else {
                        return (
                            <Chip
                                label={'Active'}
                                sx={{
                                    backgroundColor: '#008577',
                                    color: '#fff',
                                }}
                            />
                        );
                    }
                },
            },
        },
        {
            name: 'lastUpdated',
            label: 'Last Update',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.formatDate(value, 'L LTS');
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'actions',
            label: ' ',
            options: {
                filter: false,
                sort: false,
                setCellProps: () => ({
                    style: { width: '9%', cursor: 'auto' },
                }),
                // eslint-disable-next-line react/display-name
                customBodyRender: (value: any, tableMeta: any) => {
                    let actions = activeActionOptions;
                    if (tableMeta.rowData[3] === 'PENDING') {
                        actions = pendingActionOptions;
                    } else if (
                        tableMeta.rowData[3] !== 'PENDING' &&
                        tableMeta.rowData[3] !== undefined &&
                        tableMeta.rowData[3] !== 'ACCEPTED'
                    ) {
                        actions = inactiveActionOptions;
                    }

                    if (
                        user.name === tableMeta.rowData[1] ||
                        (userRole &&
                            userRole === 'ADMIN' &&
                            tableMeta.rowData[2] === 'OWNER')
                    ) {
                        return;
                    }
                    return (
                        <div>
                            <IconButton
                                size={'small'}
                                aria-label="more"
                                id="long-button"
                                aria-controls={
                                    actionOpen ? 'long-menu' : undefined
                                }
                                aria-expanded={actionOpen ? 'true' : undefined}
                                aria-haspopup="true"
                                onClick={(e) =>
                                    handleActionClick(e, tableMeta.rowData[0])
                                }
                                key={tableMeta.rowData[0]}
                            >
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                id="long-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'long-button',
                                }}
                                anchorEl={anchorEl}
                                open={actionOpen === tableMeta.rowData[0]}
                                onClose={handleCloseAction}
                                PaperProps={{
                                    style: {
                                        width: '20ch',
                                    },
                                }}
                            >
                                {actions.map((option) => {
                                    return (
                                        <MenuItem
                                            key={option.key}
                                            selected={false}
                                            onClick={(e) =>
                                                handleAction(
                                                    e,
                                                    option.key,
                                                    tableMeta
                                                )
                                            }
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant={'body2'}
                                                    >
                                                        {option.value}
                                                    </Typography>
                                                }
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Menu>
                        </div>
                    );
                },
            },
        },
    ];

    const counts = [
        {
            icon: <img src={activeUserIcon} alt="" width={35} />,
            title: 'Total Active',
            color: 'linear-gradient(to right, #008577 40%, #00857799) !important',
            data: (workspaceAccessList && workspaceAccessList.total) || 0,
        },
        {
            icon: <img src={pendingUserIcon} alt="" width={35} />,
            title: 'Total Pending',
            color: 'linear-gradient(to right, #663299 40%, #66329999) !important',
            data: (workspacePendingList && workspacePendingList.total) || 0,
        },
    ];

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Grid
                    container
                    justifyContent={'space-between'}
                    alignItems={'flex-end'}
                >
                    <Grid item>
                        <Button
                            variant={'contained'}
                            startIcon={<AddIcon />}
                            onClick={() => setOpenInvite(true)}
                        >
                            Send Invite
                        </Button>
                    </Grid>
                    <Grid item xs={5}>
                        <Grid container spacing={1}>
                            {counts.map((count, i: number) => {
                                return (
                                    <Grid item xs={6} key={`counts-${i}`}>
                                        <Paper
                                            elevation={4}
                                            sx={{
                                                p: 1,
                                                backgroundImage: count.color,
                                            }}
                                        >
                                            <Grid
                                                container
                                                alignItems={'center'}
                                            >
                                                <Grid item xs={3}>
                                                    {count.icon}
                                                </Grid>
                                                <Grid item>
                                                    <Typography
                                                        variant={'body2'}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        {count.title}
                                                    </Typography>
                                                    <Typography
                                                        variant={'h5'}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        {count.data}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} sx={{ width: '100%' }}>
                <Paper elevation={0} sx={{ p: '0px 16px 0px 16px' }}>
                    <Table
                        loading={workspaceAccessListLoading}
                        handleTableChange={(action, tableState) =>
                            handleTableChange('ACCESS', action, tableState)
                        }
                        count={workspaceAccessList && workspaceAccessList.total}
                        columns={columns}
                        pageLimit={accessPageLimit}
                        pageNumber={accessPageNumber}
                        sortOrder={{
                            direction: accessSortDirection.toLowerCase(),
                            name: accessSortField,
                        }}
                        data={workspaceAccessList && workspaceAccessList.items}
                        title={'Workspace Active Users'}
                        filter={false}
                        search={false}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12} sx={{ width: '100%' }}>
                <Paper elevation={0} sx={{ p: '0px 16px 0px 16px' }}>
                    <Table
                        loading={workspacePendingListLoading}
                        handleTableChange={(action, tableState) =>
                            handleTableChange('STATUS', action, tableState)
                        }
                        count={
                            workspacePendingList && workspacePendingList.total
                        }
                        columns={columns}
                        pageLimit={statusPageLimit}
                        pageNumber={statusPageNumber}
                        sortOrder={{
                            direction: statusSortDirection.toLowerCase(),
                            name: statusSortField,
                        }}
                        data={
                            workspacePendingList && workspacePendingList.items
                        }
                        title={'Pending Invitations'}
                        filter={false}
                        search={false}
                    />
                </Paper>
            </Grid>
            <InviteDialog
                open={openInvite}
                handleClose={() => setOpenInvite(false)}
                handleInviteUser={handleInviteUser}
                handleChangeRole={handleChangeRole}
                changeRoleEmail={changeRoleEmail}
                changeRoleRole={changeRoleRole}
                setChangeRoleRole={setChangeRoleRole}
                setChangeRoleEmail={setChangeRoleEmail}
            />
        </Grid>
    );
};

export default Security;
